import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined'
import { Box, Stack, SvgIconProps, Typography } from '@mui/material'
import { ComponentType, ReactNode, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { WaterQualityIndexSelectors } from '../../../../store/waterQualityIndex'
import { WaterTestKitRequestsSelectors } from '../../../../store/waterTestKitRequests'
import { deriveHomeAgeFlags } from '../TestMonitorProtect/PillarCard'

const MICROBIOLOGICAL_KEYWORDS = [
  'e. coli',
  'e.coli',
  'total coliform',
  'coliform',
  'turbidity',
  'legionella',
]

const matchesAny = (value: string | undefined, keywords: string[]) => {
  if (!value) return false
  const lowered = value.toLowerCase()
  return keywords.some((kw) => lowered.includes(kw))
}

interface ReasonRow {
  Icon: ComponentType<SvgIconProps>
  iconColor: string
  label: string
  body: string
}

export const SmartHomeContextCard = () => {
  const utility = useSelector(WaterQualityIndexSelectors.selectUtility)
  const waterSourceType = useSelector(WaterQualityIndexSelectors.selectWaterSourceType)
  const groupedSignals = useSelector(
    WaterQualityIndexSelectors.selectContributorsByUICategory
  )
  const latestTestKitRequest = useSelector(WaterTestKitRequestsSelectors.makeLatestResult)

  const reasons = useMemo<ReasonRow[]>(() => {
    const out: ReasonRow[] = []

    // Condition 1 — well water
    if (waterSourceType === 'well') {
      out.push({
        Icon: WaterDropOutlinedIcon,
        iconColor: 'primary.main',
        label: 'No utility backup',
        body:
          "You're on well water — no utility is testing on your behalf. The Smart Home Monitor provides continuous disinfection detection that a standard filter can't match.",
      })
    }

    // Condition 2 — microbiological violations on the utility
    const microbiological = (utility?.violations ?? []).some(
      (v) =>
        matchesAny(v.violationCategory, MICROBIOLOGICAL_KEYWORDS) ||
        matchesAny(v.contaminantName, MICROBIOLOGICAL_KEYWORDS)
    )
    if (microbiological) {
      out.push({
        Icon: WarningAmberOutlinedIcon,
        iconColor: 'error.main',
        label: 'Microbiological violation history',
        body:
          "Your utility has logged microbiological violations. The Smart Home Monitor's UV disinfection capability provides a critical additional layer of protection.",
      })
    }

    // Conditions 3 + 4 — home age driven
    const { isOlderHome, isNewerHome } = deriveHomeAgeFlags(
      latestTestKitRequest?.home_age ?? null
    )
    if (isOlderHome) {
      out.push({
        Icon: HomeOutlinedIcon,
        iconColor: 'warning.main',
        label: 'Older home — corrosion risk',
        body:
          'Older pipe materials corrode over time. Daily monitoring catches pressure and quality changes that signal early leaks before they become expensive repairs.',
      })
    }

    const hasChloramineStress = groupedSignals.some((group) =>
      group.contributors.some(
        (signal) => signal.signalKey === 'chloramine_plumbing_stress_context'
      )
    )
    if (isNewerHome && hasChloramineStress) {
      out.push({
        Icon: ScienceOutlinedIcon,
        iconColor: 'primary.main',
        label: 'PEX piping + chloramine detected',
        body:
          'Chloramine reacts with PEX piping — common in homes built after 2000 — causing microscopic pinhole leaks. Continuous monitoring catches this early.',
      })
    }

    return out
  }, [
    waterSourceType,
    utility?.violations,
    latestTestKitRequest?.home_age,
    groupedSignals,
  ])

  if (reasons.length === 0) return null

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'primary.light',
        borderRadius: 2,
        p: 2.5,
        mt: 1.5,
        bgcolor: 'blue.50', // #E6EEF4 — the soft "primary xlight" tint
      }}
    >
      <Typography
        variant="body2"
        fontWeight={700}
        color="primary.dark"
        sx={{ mb: 1.5 }}
      >
        Why Smart Home might be right for you
      </Typography>
      <Stack spacing={1.5}>
        {reasons.map((reason): ReactNode => (
          <Stack
            key={reason.label}
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
          >
            <reason.Icon sx={{ color: reason.iconColor, fontSize: 22, mt: '2px' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={700} component="span">
                {reason.label}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                sx={{ ml: 0.75 }}
              >
                — {reason.body}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}
